import PaymentAcceptanceChoice from "../components/PaymentAcceptanceChoice";
import ElakayImage from '../../../components/images/ElakayImage';
import ProductShopSelector from "../../shops/ProductShopSelector";
import { dropshippingText, useDropshippingText } from "../i18n";
import { simulateSellerProduct } from "../services/wholesaleService";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../../Layout/Layout";
import { toImageUrl } from "../../../config";
import { isAuthenticated } from "../../auth";
import { getCatalogProductForSeller, linkProviderProductToShop } from "../services/providerProductService";
import ProviderBadge from "../components/ProviderBadge";
import ProviderTierPriceTable from "../components/ProviderTierPriceTable";
import DropshippingInterneLayout from "../components/DropshippingInterneLayout";
import useMyLinkedProviderProductIds from "../hooks/useMyLinkedProviderProductIds";
import { countryOfOrigin } from "../utils/countryOfOrigin";
import { elakayAutoText } from "../../pageInside/dataElakay";
import SimulationTaxInput from "../../pageInside/SimulationTaxInput";
import "../seller/DropshippingIn.css";
import "./DropshippingInAddProduct.css";

// "Add Product - Dropshipping In" -- ticket §9/§10/§42 : Sell Your Product
// (includes/pages/addProduct.jsx) est le MODÈLE de référence (mêmes tokens
// visuels -- rayons 16-24px, ombres teintées teal rgba(0,115,153,x), inputs
// padding 0.875rem/1rem, focus-shadow teal, voir addProduct.css). Ce n'est
// PAS un clone de ses ~1900 lignes (addProduct.jsx reste monolithique et
// n'est volontairement pas touché, ticket §2/§10) : le formulaire est plus
// simple par nature (§11 -- le produit existe déjà chez un Provider, il n'y
// a rien à créer "from scratch", seulement à importer + configurer le prix
// de vente). Réutilise le vrai Layout Elakay (Header/Sidebar/Footer),
// exactement comme DropshippingIn.jsx/CJDropshippingDashboard.jsx.
const formatUsd = (value) =>
  (Number(value) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// spec §31 -- localization : suit exactement le même pattern que
// addProduct.jsx's __elakayText (namespace frontElakayHardcodedText, une clé
// tNNN par chaîne), avec un try/catch en plus car infoSource peut être `{}`
// dans les tests (DropshippingInAddProduct.test.jsx) -- addProduct.jsx ne se
// prête pas à ce cas puisqu'il n'a pas de test unitaire équivalent.
const buildTranslator = (infoSource) => (key, fallback) => {
  try {
    return (
      infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.[
        "dropshippingInAddProduct"
      ]?.[key] || fallback
    );
  } catch {
    return fallback;
  }
};

// spec §12 -- best-effort category name : categoryPath est un snapshot Mixed
// (voir ProviderProduct.js) -- pas encore alimenté par un vrai category
// picker côté Provider (ProviderProductForm.jsx n'a qu'un champ id texte),
// donc peut être null, une chaîne, un objet {name} ou une liste d'ancêtres
// [{name}, ...]. On n'ajoute jamais de nouvel appel API rien que pour ce
// libellé -- on affiche seulement ce que le payload contient déjà.
const resolveCategoryLabel = (categoryPath) => {
  if (!categoryPath) return "";
  if (typeof categoryPath === "string") return categoryPath;
  if (Array.isArray(categoryPath)) {
    return categoryPath.map((c) => (typeof c === "string" ? c : c?.name)).filter(Boolean).join(" › ");
  }
  return categoryPath.name || "";
};

const DropshippingInAddProduct = ({ infoSource }) => {
  const navigate = useNavigate();
  const { providerProductId } = useParams();
  const { linkedProductIds, loaded: linksLoaded } = useMyLinkedProviderProductIds();
  const t = buildTranslator(infoSource);
  const tw = dropshippingText(infoSource, "providerProductWizard");
  const ct = useDropshippingText(infoSource, "dropshippingCommercial");

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [shopIds, setShopIds] = useState(undefined);
  const [sellerTitle, setSellerTitle] = useState("");
  const [sellerDescription, setSellerDescription] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [compareAtPrice, setCompareAtPrice] = useState("");
  // Pricing intent: the Seller either types the price or the net profit they want per unit. Every number (fees, profit,
  // the price derived from a desired profit, whether activation is allowed) is computed by the BACKEND.
  const [pricingMode, setPricingMode] = useState("selling_price");
  const [desiredProfit, setDesiredProfit] = useState("");
  const [taxRate, setTaxRate] = useState(0);
  // Filtres vendus par CE Seller : toujours un SOUS-ENSEMBLE de ce que le Provider
  // offre (pré-rempli avec tout). Le Seller peut décocher, jamais ajouter : le
  // backend rejette de toute façon tout filtre/valeur hors source.
  const [subset, setSubset] = useState({});
  const [acceptanceMode, setAcceptanceMode] = useState("seller");

  // Simulation financière calculée PAR LE BACKEND (jamais localement) pour chaque mode.
  const [sim, setSim] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // spec §11 -- galerie d'images (Sell Your Product en affiche plusieurs via
  // ImagesLoading/ShowimagesLoading) : ProviderProduct.images est déjà un
  // tableau complet, jamais un seul cliché -- affichée telle quelle, jamais
  // recopiée/téléversée à nouveau (spec §11 -- "utilisées directement").
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // spec §2/§28/§29 -- toujours rechargé depuis l'URL (id) via l'API, jamais
  // uniquement depuis location.state : un refresh navigateur direct sur
  // /seller/provider-products/add/:providerProductId doit fonctionner à
  // l'identique.
  const load = () => {
    setLoading(true);
    setLoadError("");
    setActiveImageIndex(0);
    const token = isAuthenticated()?.token;
    getCatalogProductForSeller(token, providerProductId).then((result) => {
      if (result?.error) {
        setLoadError(result.error);
      } else {
        setProduct(result);
        setAcceptanceMode(result?.sellerLink?.acceptanceMode === "provider" && !result?.sellerLink?.paymentAcceptanceNeedsConfirmation && result?.dropshippingPayment?.sellerPaymentAcceptanceAllowed ? "provider" : "seller");
        // spec §10/§22 -- préremplit avec l'original Provider (jamais vide),
        // mais reste éditable par le Seller.
        setSellerTitle(result.name || "");
        setSellerDescription(result.description || "");
        setSubset(Object.fromEntries((result.sourceFilters || []).map((f) => [String(f.filterId), [...f.values]])));
      }
      setLoading(false);
    });
  };

  useEffect(load, [providerProductId]);

  const selectedFilterCount = Object.values(subset).reduce((n, values) => n + (values?.length ? 1 : 0), 0);
  const hasEligibleFilters = (product?.sourceFilters || []).length > 0;

  // Estimation par mode (Simple Dropshipping / stock acheté) + rentabilité -- lecture seule, relue côté serveur, anti-rebond.
  const desiredValue = Number(desiredProfit);
  useEffect(() => {
    const price = Number(sellingPrice);
    const ready = pricingMode === "desired_profit" ? desiredValue > 0 : price > 0;
    if (!product || !ready) { setSim(null); return undefined; }
    setSim(null);
    let live = true;
    const timer = setTimeout(async () => {
      const r = await simulateSellerProduct(isAuthenticated()?.token, product._id, {
        sellerPrice: pricingMode === "desired_profit" ? undefined : price, quantity: 1, pricingMode, desiredProfit: desiredProfit === "" ? undefined : desiredValue,
        taxRate: Number(taxRate) || 0,
      });
      if (live) setSim(r?.error ? null : r);
    }, 350);
    return () => { live = false; clearTimeout(timer); };
  }, [sellingPrice, product, pricingMode, desiredProfit, desiredValue, taxRate]);

  const profit = sim?.profitability || null;
  // Desired Profit mode: the price shown/sent is the one the server derived from the real fee model.
  const effectivePrice = pricingMode === "desired_profit" ? (profit?.sellingPrice || 0) : Number(sellingPrice) || 0;
  const canActivate = Boolean(profit?.complete && profit?.activation?.ok);

  // intent "draft" = Save Draft (always allowed, product stays OFF); "active" = Activate & Add (server re-checks profitability).
  const submitWith = async (intent) => {
    setSubmitError("");
    // At least ONE product filter must be selected (the server enforces the same rule).
    if (!hasEligibleFilters) { setSubmitError(ct("noFilters")); return; }
    if (selectedFilterCount < 1) { setSubmitError(ct("filterRequired")); return; }
    const priceValue = pricingMode === "desired_profit" ? effectivePrice : Number(sellingPrice);
    if (!(priceValue > 0)) {
      setSubmitError(t("t051", "Enter a valid selling price."));
      return;
    }
    if (intent === "active" && !canActivate) return;
    const visibility = intent;
    setSubmitting(true);
    const token = isAuthenticated()?.token;
    // spec §22/§23/§31 -- le frontend n'envoie que les champs Seller ;
    // providerId/providerPrice/providerStock/tierPrices ne sont JAMAIS
    // envoyés -- le backend relit lui-même le ProviderProduct pour les
    // déterminer, jamais fait confiance au client.
    const result = await linkProviderProductToShop(token, {
      boutiqueId: shopIds,
      providerProductId: product._id,
      sellerPrice: priceValue,
      compareAtPrice: compareAtPrice ? Number(compareAtPrice) : undefined,
      visibility,
      pricingMode,
      taxRate: Number(taxRate) || 0,
      ...(desiredProfit === "" ? {} : { desiredProfit: desiredValue }),
      sellerTitle,
      sellerDescription,
      filterSubset: (product.sourceFilters || [])
        .map((f) => ({ filterId: f.filterId, values: subset[String(f.filterId)] || [] }))
        .filter((f) => f.values.length > 0),
      ...(acceptanceMode === "provider" ? { acceptanceMode } : {}),
    });
    setSubmitting(false);
    if (result?.error) {
      setSubmitError(["REGION_BLOCKED", "REGION_NOT_ALLOWED", "SELLER_COUNTRY_UNKNOWN"].includes(result.code) ? tw("t190")
        : result.code === "PROFIT_BLOCKED" ? ct(result.reasons?.includes("DESIRED_PROFIT_NOT_REACHED") ? "dsi_msgDesiredNotReached" : "dsi_msgNotProfitable")
        : result.code === "FILTER_REQUIRED" ? ct("filterRequired") : result.code === "NO_ELIGIBLE_FILTERS" ? ct("noFilters") : result.error);
      return;
    }
    navigate("/seller/provider-products");
  };
  const handleSubmit = (e) => { e.preventDefault(); return submitWith("active"); };

  const priceValue = effectivePrice;
  const galleryImages = product?.images?.filter(Boolean) || [];
  const categoryLabel = resolveCategoryLabel(product?.categoryPath);

  // spec §20/§47 (Test Scenario 2) -- déjà ajouté : ne jamais permettre un
  // second import du même ProviderProduct, ni même montrer le formulaire.
  const alreadyAdded = linksLoaded && product ? linkedProductIds.has(String(product._id)) : false;
  // spec §9/§48 (Test Scenario 3) -- dropshipping désactivé sur CE produit :
  // wholesale uniquement, Add Product Dropshipping In reste indisponible.
  const wholesaleOnly = product ? !product.dropshippingEnabled : false;

  return (
    <Layout
      infoSource={infoSource}
      titleElakay="dropshippingInAddProduct"
      SliderPunShow={false}
      loading={false}
      outOffPage={false}
      {...infoSource?.allSettings?.layout}
      showFooter={true}
    >
      <DropshippingInterneLayout infoSource={infoSource}>
      <div className="dsi-page dsia-page">
        <nav className="dsi-breadcrumb" aria-label={elakayAutoText("h_c766e665183df1", "Breadcrumb")}>
          <Link to="/myShop">{t("t001", "Sell Center")}</Link>
          <span aria-hidden="true">›</span>
          <Link to="/seller/provider-products">{t("t002", "My Products")}</Link>
          <span aria-hidden="true">›</span>
          <span aria-current="page">{t("t003", "Add Product")}</span>
        </nav>

        {loading && (
          <div className="dsi-skeleton" data-testid="dsi-skeleton" aria-hidden="true" aria-busy="true">
            <div className="dsi-skeleton__shop" />
          </div>
        )}

        {!loading && loadError && (
          <div className="dsi-state-card" role="alert">
            <div className="dsi-state-card__icon" aria-hidden="true">⚠️</div>
            <h2>{t("t004", "This product isn't available")}</h2>
            <p>{loadError}</p>
            <Link to="/seller/providers" className="dsi-state-card__btn">← {t("t005", "Back to Providers")}</Link>
          </div>
        )}

        {!loading && !loadError && product && alreadyAdded && (
          <div className="dsi-state-card">
            <div className="dsi-state-card__icon" aria-hidden="true">✓</div>
            <h2>{t("t006", "Already Added")}</h2>
            <p>{product.name} {t("t007", "is already in your shop.")}</p>
            <button type="button" className="dsi-state-card__btn" onClick={() => navigate("/seller/provider-products")}>
              {t("t008", "Manage Product")}
            </button>
          </div>
        )}

        {!loading && !loadError && product && !alreadyAdded && wholesaleOnly && (
          <div className="dsi-state-card">
            <div className="dsi-state-card__icon" aria-hidden="true">📦</div>
            <h2>{t("t009", "Wholesale Only")}</h2>
            <p>{t("t010", "This product is available for wholesale only. Dropshipping is not enabled by this provider.")}</p>
            <Link to={`/seller/provider-products/${providerProductId}`} className="dsi-state-card__btn">
              ← {t("t011", "Back to Product")}
            </Link>
          </div>
        )}

        {!loading && !loadError && product && !alreadyAdded && !wholesaleOnly && (
          <form className="dsia-form" onSubmit={handleSubmit}>
            <ProductShopSelector value={shopIds} onChange={setShopIds} />
            <div className="dsia-title-card">
              <h1>{t("t003", "Add Product")}</h1>
              <p className="dsi-hero__subtitle">{t("t012", "Import this provider product into your Elakay shop.")}</p>
              <div className="dsia-provider-chip">
                <ProviderBadge label={t("t013", "Provider")} />
              </div>
            </div>

            {/* spec §5 -- Provider Source, toujours lecture seule : identité
                du Provider jamais falsifiable par le Seller. */}
            <section className="dsia-section">
              <h2>{t("t014", "Provider / Producer Source")}</h2>
              <div className="dsia-readonly-grid">
                <div>
                  <span className="dsia-label">{t("t015", "Provider Shop")}</span>
                  <span className="dsia-readonly-value">{product.shop?.shopName || t("t013", "Provider")}</span>
                </div>
                {product.shop?.location && (
                  <div>
                    <span className="dsia-label">{t("t016", "Location")}</span>
                    <span className="dsia-readonly-value">{product.shop.location}</span>
                  </div>
                )}
                <div>
                  <span className="dsia-label">{t("t017", "Provider Product ID")}</span>
                  <span className="dsia-readonly-value">{product.publicId || product._id}</span>
                </div>
                <div>
                  <span className="dsia-label">{t("t018", "Status")}</span>
                  <span className="dsia-readonly-value">{product.status}</span>
                </div>
                {(product.sku || product.providerSku) && (
                  <div>
                    <span className="dsia-label">{t("t020", "Provider SKU")}</span>
                    <span className="dsia-readonly-value">{product.providerSku || product.sku}</span>
                  </div>
                )}
                {/* Made In / Country of Origin (ticket part 14): read-only here too -- the Provider
                    controls this data, the Seller only ever reads it, never sets it. */}
                {countryOfOrigin(product.countryOfOrigin) && (
                  <div>
                    <span className="dsia-label">{elakayAutoText("h_dsia_made_in", "Made In")}</span>
                    <span className="dsia-readonly-value">{countryOfOrigin(product.countryOfOrigin).name} {countryOfOrigin(product.countryOfOrigin).flag}</span>
                  </div>
                )}
                {categoryLabel && (
                  <div>
                    <span className="dsia-label">{t("t024", "Category")}</span>
                    <span className="dsia-readonly-value">{categoryLabel}</span>
                  </div>
                )}
              </div>
            </section>

            <section className="dsia-section">
              <h2>{t("t021", "Product Information")}</h2>
              <div className="dsia-product-info">
                {galleryImages.length > 0 ? (
                  <div className="dsia-gallery">
                    <ElakayImage
                      preset="product-detail"
                      className="dsia-gallery__main"
                      src={toImageUrl(galleryImages[activeImageIndex] || galleryImages[0])}
                      alt={product.name}
                      loading="lazy"
                    />
                    {galleryImages.length > 1 && (
                      <div className="dsia-gallery__thumbs">
                        {galleryImages.map((img, idx) => (
                          <button
                            type="button"
                            key={img + idx}
                            className={`dsia-gallery__thumb${idx === activeImageIndex ? " dsia-gallery__thumb--active" : ""}`}
                            onClick={() => setActiveImageIndex(idx)}
                            aria-label={`${product.name} ${idx + 1}`}
                            aria-pressed={idx === activeImageIndex}
                          >
                            <ElakayImage preset="product-thumb" src={toImageUrl(img)} alt="" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="dsia-no-image" aria-hidden="true" />
                )}
                <div>
                  <p className="dsia-readonly-value dsia-readonly-value--title">{product.name}</p>
                  {product.description && <p className="dsia-description">{product.description}</p>}
                </div>
              </div>

              {!hasEligibleFilters && <p className="dsi-empty-filtered" role="alert" data-testid="no-filters">{ct("noFilters")}</p>}
              {(product.categories?.length > 0 || product.sourceFilters?.length > 0) && (
                <div className="dsia-field-grid" style={{ marginTop: 14 }}>
                  {product.categories?.length > 0 && (
                    <div className="dsia-field">
                      <span className="dsia-label">{tw("t158")}</span>
                      <span className="dsia-readonly-value">{product.categories.map((c) => (Array.isArray(c.name) ? c.name.join(" › ") : c.name)).join(" · ")}</span>
                    </div>
                  )}
                  {product.sourceFilters?.length > 0 && (
                    <fieldset className="dsia-field" data-testid="filters-fieldset">
                      <legend className="dsia-label">{ct("selectFilters")}</legend>
                      <small className="text-muted d-block mb-2">{ct("selectFiltersHint")}</small>
                      {product.sourceFilters.map((f) => (
                        <div key={String(f.filterId)} className="mb-2">
                          <strong>{f.name}</strong>
                          <div>
                            {f.values.map((value) => {
                              const chosen = (subset[String(f.filterId)] || []).includes(value);
                              return (
                                <label key={value} className="mr-3">
                                  <input
                                    type="checkbox"
                                    checked={chosen}
                                    onChange={() =>
                                      setSubset((prev) => {
                                        const current = prev[String(f.filterId)] || [];
                                        return { ...prev, [String(f.filterId)]: chosen ? current.filter((v) => v !== value) : [...current, value] };
                                      })
                                    }
                                  />{" "}
                                  {value}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </fieldset>
                  )}
                </div>
              )}

              {/* spec §10/§22 -- éditable, jamais vide (préremplit avec
                  l'original Provider ci-dessus, conservé tel quel comme
                  référence). */}
              <div className="dsia-field-grid" style={{ marginTop: 14 }}>
                <label className="dsia-field">
                  <span className="dsia-label">{t("t022", "Seller Product Title")}</span>
                  <input
                    type="text"
                    value={sellerTitle}
                    onChange={(e) => setSellerTitle(e.target.value)}
                  />
                </label>
              </div>
              <label className="dsia-field" style={{ marginTop: 12 }}>
                <span className="dsia-label">{t("t023", "Seller Marketing Description")}</span>
                <textarea
                  rows={3}
                  value={sellerDescription}
                  onChange={(e) => setSellerDescription(e.target.value)}
                />
              </label>
            </section>

            <section className="dsia-section">
              <h2>{t("t025", "Provider Information")}</h2>
              <p className="dsi-hero__subtitle" style={{ margin: "0 0 12px" }}>
                {t("t026", "Controlled by the provider -- read only.")}
              </p>
              <div className="dsia-readonly-grid">
                <div>
                  <span className="dsia-label">{t("t027", "Provider Price")} — {ct("simDs")}</span>
                  <span className="dsia-readonly-value" data-testid="ds-cost">{formatUsd(product.terms?.simpleDropshipping?.unitPrice ?? product.basePrice)}</span>
                </div>
                <div>
                  <span className="dsia-label">{ct("simLogistics")}</span>
                  <span className="dsia-readonly-value" data-testid="ds-logistics">
                    {(() => { const l = product.terms?.simpleDropshipping?.logistics; if (!l || l.offered === false) return "—"; if (!l.known) return ct("feeToConfirm"); return l.free ? ct("feeFreeLabel") : `${formatUsd(l.amount)}${l.type === "per_unit" ? ` ${ct("perUnit")}` : ""}`; })()}
                  </span>
                </div>
                <div>
                  <span className="dsia-label">{t("t028", "Available")}</span>
                  <span className="dsia-readonly-value">{product.stock ?? 0}</span>
                  <span className="dsia-sublabel">{t("t029", "Inventory Source: Provider")}</span>
                </div>
                <div>
                  <span className="dsia-label">{t("t030", "Minimum Quantity")}</span>
                  <span className="dsia-readonly-value">{product.minimumOrderQuantity || 1}</span>
                </div>
                <div>
                  <span className="dsia-label">{t("t031", "Fulfillment")}</span>
                  <span className="dsia-readonly-value">
                    {product.shippingByProviderEnabled ? t("t032", "Provider Dropshipping") : t("t033", "Not specified")}
                  </span>
                </div>
              </div>
            </section>

            <section className="dsia-section dsia-payment-card" data-testid="payment-acceptance-choice">
              <h2>{t("paymentAcceptanceTitle", "Payment acceptance")}</h2>
              <PaymentAcceptanceChoice rules={product.dropshippingPayment} mode={acceptanceMode} onChange={setAcceptanceMode} />
            </section>

            {(product.wholesaleEnabled || product.quantityPricing?.length > 0) && (
              <section className="dsia-section">
                <h2>{t("t034", "Wholesale Information")}</h2>
                <div className="dsia-readonly-grid">
                  <div>
                    <span className="dsia-label">{t("t035", "Wholesale Enabled")}</span>
                    <span className="dsia-readonly-value">
                      {product.wholesaleEnabled ? t("t036", "Yes") : t("t037", "No")}
                    </span>
                  </div>
                </div>

                {product.quantityPricing?.length > 0 && (
                  <>
                    <p className="dsia-label" style={{ marginTop: 16 }}>{t("t039", "Wholesale tier pricing")}</p>
                    <ProviderTierPriceTable tiers={product.quantityPricing} />
                  </>
                )}
              </section>
            )}

            <section className="dsia-section">
              <h2>{t("t040", "Your Selling Information")}</h2>
              {/* Check buttons exclusifs : cocher un mode désactive l'autre
                  (un mode est toujours actif). L'input natif reste masqué par
                  mdb.css, la case visible est le span .dsia-mode-check. */}
              <fieldset className="dsia-mode-group" aria-label={ct("dsi_pricingMode")}>
                <span className="dsia-label">{ct("dsi_pricingMode")}</span>
                <div className="dsia-mode-options">
                  {[["selling_price", "dsi_modeSellingPrice"], ["desired_profit", "dsi_modeDesiredProfit"]].map(([value, key]) => (
                    <label key={value} className={`dsia-mode-option${pricingMode === value ? " is-active" : ""}`}>
                      <input type="checkbox" name="pricingMode" value={value} checked={pricingMode === value} onChange={() => setPricingMode(value)} />
                      <span className="dsia-mode-check" aria-hidden="true" />
                      {ct(key)}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="dsia-field-grid">
                <label className="dsia-field">
                  <span className="dsia-label">{t("t041", "Selling Price")}</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="$0.00"
                    value={pricingMode === "desired_profit" ? (profit?.sellingPrice ?? "") : sellingPrice}
                    onChange={(e) => setSellingPrice(e.target.value)}
                    readOnly={pricingMode === "desired_profit"}
                    required={pricingMode !== "desired_profit"}
                  />
                </label>
                <label className="dsia-field">
                  <span className="dsia-label">{ct("dsi_desiredProfit")}</span>
                  <input type="number" min="0.01" step="0.01" placeholder="$0.00" value={desiredProfit} onChange={(e) => setDesiredProfit(e.target.value)} />
                </label>
                <label className="dsia-field">
                  <span className="dsia-label">{t("t042", "Compare At Price (optional)")}</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="$0.00"
                    value={compareAtPrice}
                    onChange={(e) => setCompareAtPrice(e.target.value)}
                  />
                </label>
              </div>

              <SimulationTaxInput infoSource={infoSource} value={taxRate} onChange={setTaxRate} />
              {priceValue > 0 && sim?.simpleDropshipping && sim?.purchasedStock && (
                <div className="dsia-margin-card" data-testid="simulation">
                  <h3 style={{ fontSize: "1rem", margin: "0 0 8px" }}>{ct("simTitle")}</h3>
                  <table className="dsia-sim">
                    <thead><tr><th /><th>{ct("simDs")}</th><th>{ct("simPurchased")}</th></tr></thead>
                    <tbody>
                      {[["simCost", (m) => m.productCost], ["simLogistics", (m) => (m.logistics.offered ? (m.logistics.known ? m.logistics.perUnit : null) : 0)], ["simGross", (m) => m.grossBeforeOtherFees]].map(([k, get]) => (
                        <tr key={k}><th scope="row">{ct(k)}</th>{[sim.simpleDropshipping, sim.purchasedStock].map((m, i) => (
                          <td key={i}>{!m.enabled ? <em>{ct("simDisabled")}</em> : get(m) === null ? <em>{ct("feeToConfirm")}</em> : formatUsd(get(m))}</td>))}</tr>
                      ))}
                    </tbody>
                  </table>
                  {(sim.simpleDropshipping.unknownFees.length > 0 || sim.purchasedStock.unknownFees.length > 0) && <p className="dsia-margin-disclaimer">{ct("simUnknown")}</p>}
                  <p className="dsia-margin-disclaimer">{ct("simDisclaimer")}</p>
                </div>
              )}

              {profit?.complete && (
                <div className="dsia-margin-card" data-testid="profitability">
                  <h3 style={{ fontSize: "1rem", margin: "0 0 8px" }}>{ct("dsi_costBreakdown")}</h3>
                  <table className="dsia-sim"><tbody>
                    <tr><th scope="row">{ct("dsi_providerCost")}</th><td>{formatUsd(profit.productCost)}</td></tr>
                    <tr><th scope="row">{ct("dsi_fulfillmentCost")}</th><td>{formatUsd(profit.logisticsCost)}</td></tr>
                    <tr><th scope="row">{ct("dsi_elakayFees")}</th><td>{formatUsd(profit.elakayFees)}</td></tr>
                    <tr><th scope="row">{ct("dsi_paymentFees")}</th><td>{formatUsd(profit.paymentFees)}</td></tr>
                    <tr><th scope="row">{ct("dsi_applicableTaxes")}</th><td>{formatUsd(profit.taxAmount ?? 0)}</td></tr>
                    <tr><th scope="row">{ct("dsi_netProfit")}</th><td data-testid="net-profit"><strong>{formatUsd(profit.netProfit)}</strong></td></tr>
                    <tr><th scope="row">{ct("dsi_profitMargin")}</th><td>{profit.marginPercent}%</td></tr>
                  </tbody></table>
                  <p className="dsia-margin-disclaimer">{ct("dsi_taxNote")}</p>
                  {profit.unknownCosts?.length > 0 && <p className="dsia-margin-disclaimer">{ct("simUnknown")}</p>}
                </div>
              )}
              {profit && (
                <p className={canActivate ? "dsia-margin-disclaimer" : "dsi-empty-filtered"} role={canActivate ? "status" : "alert"} data-testid="profit-message">
                  {!profit.complete ? ct("dsi_msgMissing")
                    : canActivate ? ct("dsi_msgProfitOk")
                    : ct(profit.activation.reasons.includes("DESIRED_PROFIT_NOT_REACHED") ? "dsi_msgDesiredNotReached" : "dsi_msgNotProfitable")}
                </p>
              )}
            </section>

            {submitError && <p className="dsi-empty-filtered" role="alert">{submitError}</p>}

            <div className="dsia-actions">
              <Link to={`/seller/provider-products/${providerProductId}`} className="dsia-cancel-btn">{t("t049", "Cancel")}</Link>
              <button type="button" className="dsia-cancel-btn" disabled={submitting || !hasEligibleFilters} onClick={() => submitWith("draft")}>
                {ct("dsi_saveDraft")}
              </button>
              <button type="submit" className="dsia-submit-btn" disabled={submitting || !hasEligibleFilters || !canActivate}>
                {submitting ? t("t050", "Adding…") : ct("dsi_activateAdd")}
              </button>
            </div>
          </form>
        )}
      </div>
      </DropshippingInterneLayout>
    </Layout>
  );
};

export default DropshippingInAddProduct;
